import React, { useEffect, useState } from 'react';
import { CircleDot, Eye, EyeOff, Loader2 } from 'lucide-react';

import { loadPatientHistory } from './api';
import { tx } from './i18n';
import { errorMessage } from './ui';

// Everything done that concerns one patient, newest first, from the audit log
// (XOmics GET /Patients/:id/history): edits, uploads and analyses, reports asked
// for, rows of a result hidden or shown again, and whatever is recorded against
// the patient from now on. Things done, not things looked at. A page at a time.

const WHEN = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
// The same action by the same person within this long reads as one line.
const SAME_MOMENT_MS = 2 * 60 * 1000;
// The columns that name a result row to a reader, in the order they are tried; a
// row with none of them is named by its position.
const ROW_NAMES = ['genes', 'gene', 'traits_name', 'drug_s', 'disease', 'hgvsc', 'hgvsp', 'rsid', 'id'];

function rowName(resultRow) {
  const names = [];
  for (const key of ROW_NAMES) {
    const value = resultRow.row?.[key];
    const text = value === null || value === undefined ? '' : String(value).trim();
    if (text && !names.includes(text)) names.push(text);
    if (names.length === 2) break;
  }
  return names.length > 0 ? names.join(' · ') : tx('ui.patient.activity_row_n', { n: resultRow.sourceRow });
}

// "dateOfBirth" reads as "date of birth".
const fieldWords = (field) => String(field).replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();

function eventText(event) {
  const row = event.resultRow;
  if (row) {
    return tx(row.hidden ? 'ui.patient.activity_row_hidden' : 'ui.patient.activity_row_shown', {
      row: rowName(row),
      type: row.dataType?.displayName || row.targetTable,
      visit: row.visitNumber ?? '—',
    });
  }
  return event.actionLabel || String(event.action || '').replace(/_/g, ' ').toLowerCase();
}

function eventNote(event) {
  if (event.resultRow?.reason) return tx('ui.patient.activity_reason', { reason: event.resultRow.reason });
  const fields = event.metadata?.fields;
  if (Array.isArray(fields) && fields.length > 0) return tx('ui.patient.activity_fields', { fields: fields.map(fieldWords).join(', ') });
  return null;
}

function actorName(actor) {
  if (actor?.name) return actor.name;
  return actor?.type === 'user' ? tx('ui.patient.activity_someone') : tx('ui.patient.activity_system');
}

// Consecutive rows of the same action by the same person moments apart, such as
// the parts of one upload, are one line with a count.
function grouped(events) {
  const lines = [];
  for (const event of events) {
    const last = lines[lines.length - 1];
    if (last && !event.resultRow && !last.event.resultRow && last.event.action === event.action
      && actorName(last.event.actor) === actorName(event.actor) && last.event.outcome === event.outcome
      && last.oldest - event.at <= SAME_MOMENT_MS) {
      last.count += 1;
      last.oldest = event.at;
    } else {
      lines.push({ event, count: 1, oldest: event.at });
    }
  }
  return lines;
}

export function PatientHistory({ patientId, revision = 0 }) {
  const [events, setEvents] = useState([]);
  const [nextBeforeId, setNextBeforeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    loadPatientHistory(patientId)
      .then((page) => {
        if (cancelled) return;
        setEvents(page.events);
        setNextBeforeId(page.nextBeforeId);
      })
      .catch((caught) => { if (!cancelled) setError(errorMessage(caught)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId, revision]);

  const showOlder = async () => {
    setLoadingMore(true);
    setError('');
    try {
      const page = await loadPatientHistory(patientId, { beforeId: nextBeforeId });
      setEvents((current) => [...current, ...page.events]);
      setNextBeforeId(page.nextBeforeId);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className="xo-rec-activity" aria-labelledby="xo-patient-activity-title">
      <header>
        <h3 id="xo-patient-activity-title">{tx('ui.patient.activity')}</h3>
        <p>{tx('ui.patient.activity_intro')}</p>
      </header>
      {error && <p className="xo-rec-activity-error" role="alert">{error}</p>}
      {loading ? <p className="xo-rec-activity-loading"><Loader2 className="xo-spin" aria-hidden="true" /></p> : (
        events.length === 0 ? <p className="xo-rec-activity-empty">{tx('ui.patient.activity_empty')}</p> : (
          <ol className="xo-rec-activity-list">
            {grouped(events).map(({ event, count }) => {
              const Icon = event.resultRow ? (event.resultRow.hidden ? EyeOff : Eye) : CircleDot;
              const note = eventNote(event);
              return (
                <li key={event.id} className={event.outcome === 'failed' ? 'is-failed' : ''}>
                  <span className="xo-rec-activity-icon"><Icon aria-hidden="true" /></span>
                  <div>
                    <strong>
                      {eventText(event)}
                      {count > 1 && <b>{tx('ui.patient.activity_times', { count })}</b>}
                      {event.outcome === 'failed' && <em>{tx('ui.patient.activity_failed')}</em>}
                    </strong>
                    {note && <span>{note}</span>}
                    <small>{[actorName(event.actor), WHEN.format(new Date(event.at))].join(' · ')}</small>
                  </div>
                </li>
              );
            })}
          </ol>
        )
      )}
      {!loading && nextBeforeId !== null && (
        <button type="button" className="xo-rec-activity-more" onClick={showOlder} disabled={loadingMore}>
          {loadingMore && <Loader2 className="xo-spin" aria-hidden="true" />}{tx('ui.patient.activity_more')}
        </button>
      )}
    </section>
  );
}
